import { useState } from 'react';
import { Link } from 'react-router';
import { Plus } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  PageShell,
  SectionCard,
  Skeleton,
  type DataTableColumn,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { useCreateOpenApiPartnerMutation, useOpenApiPartnersQuery } from '../hooks/useOpenApiPartners';
import { OPEN_API_PARTNER_WRITE_ROLES } from '../lib/openApiPartnerRbac';
import { formatKstDateTime, formatPeriod } from '../lib/partnerFormat';
import { PartnerForm } from '../components/PartnerForm';
import { IssuedKeysDialog, type IssuedKeyValues } from '../components/IssuedKeysDialog';
import { PartnerStatusBadge } from '../components/PartnerStatusBadge';
import type { OpenApiPartner } from '../types/openApiPartner';

/**
 * 매출 수집 API 연동 관리 — 제휴사별 partner_key·key, 허용 IP, 사용기간(고도몰 openhub 역할).
 * 조회 SUPER_ADMIN·OPS_ADMIN, 등록 SUPER_ADMIN.
 */
export const OpenApiPartnersPage = () => {
  const { t } = useTranslation('common');
  const canWrite = useHasRole(OPEN_API_PARTNER_WRITE_ROLES);
  const query = useOpenApiPartnersQuery();
  const create = useCreateOpenApiPartnerMutation();
  const [createOpen, setCreateOpen] = useState(false);
  // 발급 키 원문 — 모달에만 두고 닫으면 버린다.
  const [issued, setIssued] = useState<IssuedKeyValues | null>(null);

  const columns: DataTableColumn<OpenApiPartner>[] = [
    {
      key: 'partnerName',
      header: t('openApiPartners.fields.partnerName', '제휴사명'),
      cell: (p) => (
        <Link
          to={`/settings/open-api-partners/${encodeURIComponent(p.partnerId)}`}
          className="font-medium underline-offset-2 hover:underline"
          style={{ color: 'var(--content-strong)' }}
        >
          {p.partnerName}
        </Link>
      ),
    },
    {
      key: 'partnerKey',
      header: t('openApiPartners.columns.partnerKey', '제휴사 키'),
      width: 100,
      cell: (p) => <code className="font-mono text-[12px]">{p.partnerKeyMasked ?? '—'}</code>,
    },
    {
      key: 'shopKey',
      header: t('openApiPartners.columns.shopKey', '쇼핑몰 키'),
      width: 100,
      cell: (p) => <code className="font-mono text-[12px]">{p.shopKeyMasked ?? '—'}</code>,
    },
    {
      key: 'allowedIps',
      header: t('openApiPartners.fields.allowedIps', '허용 IP'),
      width: 150,
      cell: (p) =>
        p.allowedIps.length === 0 ? (
          '—'
        ) : (
          <span className="font-mono text-[12px]" title={p.allowedIps.join('\n')}>
            {p.allowedIps[0]}
            {p.allowedIps.length > 1
              ? ` ${t('openApiPartners.columns.moreIps', { count: p.allowedIps.length - 1, defaultValue: '외 {{count}}개' })}`
              : ''}
          </span>
        ),
    },
    {
      key: 'period',
      header: t('openApiPartners.fields.period', '사용기간'),
      width: 190,
      cell: (p) => <span className="tabular-nums">{formatPeriod(p) ?? t('openApiPartners.columns.noLimit', '제한 없음')}</span>,
    },
    { key: 'status', header: t('openApiPartners.columns.status', '상태'), width: 90, cell: (p) => <PartnerStatusBadge partner={p} /> },
    {
      key: 'lastCalledAt',
      header: t('openApiPartners.columns.lastCalledAt', '마지막 호출'),
      width: 130,
      cell: (p) => <span className="tabular-nums">{formatKstDateTime(p.lastCalledAt) ?? '—'}</span>,
    },
    {
      key: 'updatedAt',
      header: t('openApiPartners.columns.updatedAt', '수정일'),
      width: 130,
      cell: (p) => <span className="tabular-nums">{formatKstDateTime(p.updatedAt) ?? '—'}</span>,
    },
  ];

  return (
    <PageShell
      title={t('openApiPartners.title', '매출 수집 API 연동 관리')}
      description={t(
        'openApiPartners.description',
        '매출 수집 API 를 호출하는 제휴사의 키, 허용 IP, 사용기간을 관리합니다. 변경은 최대 30초 안에 모든 서버에 반영됩니다.',
      )}
      actions={
        canWrite ? (
          <Button type="button" onClick={() => setCreateOpen(true)} data-testid="open-api-partner-create">
            <Plus size={14} aria-hidden="true" />
            {t('openApiPartners.actions.create', '제휴사 등록')}
          </Button>
        ) : null
      }
    >
      <SectionCard padded={false}>
        {query.isPending ? (
          <Skeleton className="m-4 h-24" />
        ) : query.isError ? (
          <p role="alert" className="p-4 text-[13px]" style={{ color: 'var(--status-danger-fg)' }}>
            {query.error.message}
          </p>
        ) : (
          <DataTable
            columns={columns}
            rows={query.data}
            rowKey={(p) => p.partnerId}
            ariaLabel={t('openApiPartners.title', '매출 수집 API 연동 관리')}
            emptyState={t('openApiPartners.empty', '등록된 제휴사가 없습니다.')}
          />
        )}
      </SectionCard>

      <Dialog open={createOpen} onOpenChange={(o) => (!o && !create.isPending ? setCreateOpen(false) : undefined)}>
        <DialogContent className="max-w-[560px]" data-testid="open-api-partner-create-dialog">
          <DialogHeader>
            <DialogTitle>{t('openApiPartners.actions.create', '제휴사 등록')}</DialogTitle>
            <DialogDescription>
              {t('openApiPartners.createDesc', '저장하면 제휴사 키와 쇼핑몰 키가 발급됩니다. 키 원문은 저장 직후 한 번만 보입니다.')}
            </DialogDescription>
          </DialogHeader>
          {createOpen ? (
            <PartnerForm
              submitting={create.isPending}
              errorMessage={create.error?.message ?? null}
              submitLabel={t('openApiPartners.actions.createSubmit', '등록하고 키 발급')}
              onCancel={() => setCreateOpen(false)}
              onSubmit={({ active: _active, ...payload }) => {
                create.mutate(payload, {
                  onSuccess: (res) => {
                    // 원문은 모달 상태로만 넘기고 mutation 결과는 바로 비운다(캐시에 남기지 않음).
                    setIssued({ partnerName: res.partner.partnerName, partnerKey: res.partnerKey, key: res.key });
                    create.reset();
                    setCreateOpen(false);
                  },
                });
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <IssuedKeysDialog issued={issued} onClose={() => setIssued(null)} />
    </PageShell>
  );
};
